import React from 'react';
import { Order } from '../store';
import { History, Package, AlertOctagon, Wrench, Clock } from 'lucide-react';

interface OrderHistoryProps {
  orders: Order[] | null;
  isBug3Active: boolean;
  isPatched: boolean;
}

export const OrderHistory: React.FC<OrderHistoryProps> = ({
  orders,
  isBug3Active,
  isPatched,
}) => {
  // If Bug #3 is active and NOT patched, orders is null — render captured component crash state
  if (isBug3Active && !isPatched && orders === null) {
    return (
      <div
        className="error-boundary animate-fade-in"
        style={{
          background: 'rgba(239, 68, 68, 0.06)',
          border: '1px solid var(--red)',
          borderRadius: 'var(--radius-md)',
          padding: 24,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <div style={{ background: 'var(--red-soft)', padding: 12, borderRadius: '50%', color: 'var(--red)' }}>
          <AlertOctagon size={32} />
        </div>
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--red)' }}>
            Component Crash: Unhandled Exception in Render Tree
          </h3>
          <p style={{ fontSize: 12, color: 'var(--text-2)', maxWidth: 420, margin: '6px auto 0', fontFamily: 'var(--font-mono)' }}>
            <code>TypeError: Cannot read properties of null (reading 'map')</code>
          </p>
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
            <code>orders</code> is null; attempted to evaluate <code>orders.map(...)</code> inside OrderHistory.tsx
          </div>
        </div>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: 'rgba(0,0,0,0.4)',
            padding: '6px 14px',
            borderRadius: 6,
            fontSize: 11,
            fontFamily: 'var(--font-mono)',
            color: 'var(--amber)',
          }}
        >
          <Wrench size={13} /> AutoFix WebMCP Agent is diagnosing live crash dump...
        </div>
      </div>
    );
  }

  // ✅ Patched or normal state: safe array check
  const safeOrders: Order[] = Array.isArray(orders) ? orders : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h4 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <History size={14} style={{ color: 'var(--accent)' }} /> Customer Order History
        </h4>
        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{safeOrders.length} past orders</span>
      </div>

      {safeOrders.length === 0 ? (
        <div
          style={{
            padding: '20px 16px',
            textAlign: 'center',
            color: 'var(--text-3)',
            background: 'var(--bg-card)',
            borderRadius: 'var(--radius-md)',
            border: '1px dashed var(--border)',
            fontSize: 12,
          }}
        >
          <Package size={20} style={{ margin: '0 auto 6px', color: 'var(--text-3)' }} />
          No past orders found for this account session.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {safeOrders.map((order) => (
            <div
              key={order.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 12px',
                background: 'var(--bg-card)',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
                fontSize: 12,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Clock size={13} style={{ color: 'var(--text-3)' }} />
                <div>
                  <span style={{ fontWeight: 600, color: 'var(--text-1)', fontFamily: 'var(--font-mono)' }}>
                    {order.id}
                  </span>
                  <span style={{ color: 'var(--text-3)', marginLeft: 8 }}>{order.date}</span>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="badge badge-green" style={{ fontSize: 9 }}>
                  {order.status}
                </span>
                <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--text-1)' }}>
                  ${order.total.toFixed(2)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
